'use client';

import React, { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { trackEvent } from '@/lib/analytics';

type FabricItem = {
  id: string;
  name: string;
  composition: string;
  weight: string;
  description: string;
  image: string;
  alt: string;
  colSpan?: string;
  rowSpan?: string;
};

const suitingFabrics: FabricItem[] = [
  {
    id: 'pure-wool',
    name: 'Pure Wool',
    composition: '100% Wool',
    weight: '280–320 gsm',
    description:
      'Classic pure wool suiting — warm, breathable, and naturally resilient. Ideal for premium tailored suits and blazers. Manufactured in Bhilwara, Rajasthan.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_12ae40665-1768670744853.png',
    alt: 'Dark charcoal grey pure wool suiting fabric bolt with fine twill weave texture in a professional textile showroom in Bhilwara',
    colSpan: 'lg:col-span-2',
    rowSpan: 'lg:row-span-2',
  },
  {
    id: 'poly-wool',
    name: 'Poly Wool',
    composition: '65% Polyester / 35% Wool',
    weight: '240–280 gsm',
    description:
      'Durable poly-wool blend offering wrinkle resistance and easy care — the workhorse of corporate suiting.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c2e92450-1764668259344.png',
    alt: 'Navy blue poly wool suiting fabric roll with smooth surface finish displayed in a bright professional textile warehouse',
    colSpan: 'lg:col-span-1',
  },
  {
    id: 'tropical',
    name: 'Tropical Suiting',
    composition: '55% Polyester / 45% Viscose',
    weight: '180–220 gsm',
    description:
      'Lightweight open-weave suiting for warm climates. Breathable, drape-friendly, and ideal for summer collections.',
    image: 'https://images.unsplash.com/photo-1726160330235-1df4c42666ef',
    alt: 'Light beige tropical suiting fabric with open weave texture in an airy bright textile setting',
    colSpan: 'lg:col-span-1',
  },
  {
    id: 'linen-blend',
    name: 'Linen Blend',
    composition: '55% Linen / 45% Viscose',
    weight: '200–240 gsm',
    description:
      'Natural linen texture with added drape from viscose. Perfect for casual suits and summer blazers.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1398c0b99-1772894995145.png',
    alt: 'Natural linen blend suiting fabric with textured weave in warm beige tones under bright natural light',
    colSpan: 'lg:col-span-1',
  },
  {
    id: 'cashmere-blend',
    name: 'Cashmere Blend',
    composition: '80% Wool / 20% Cashmere',
    weight: '300–340 gsm',
    description:
      'The pinnacle of our suiting range — soft, luxurious, and exceptionally warm. For premium and luxury collections.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1eb79cc1a-1776538764424.png',
    alt: 'Luxurious charcoal cashmere blend suiting fabric with soft fine texture under premium dark atmospheric lighting',
    colSpan: 'lg:col-span-1',
  },
  {
    id: 'tweed',
    name: 'Tweed',
    composition: '100% Wool',
    weight: '350–400 gsm',
    description:
      'Robust Harris-style tweed with distinctive flecked texture. Perfect for heritage-inspired and winter collections.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16c5d4880-1772084684449.png',
    alt: 'Brown and grey flecked tweed suiting fabric with coarse textured weave in a rustic studio background',
    colSpan: 'lg:col-span-1',
  },
];

const suitingQualityNames = [
  'DMS',
  'Melange',
  '2/18',
  'NTRT',
  'Fancy',
  'TR / T-R',
  'Panting',
  'Gwalior Suiting',
  'Brio',
  'Post Card',
  'Trovine',
  'SNZ',
  'Polyester Suiting',
];

const shirtingQualityNames = [
  'Magic',
  'Goodwill',
  'Moti Magic',
  'Luster Venture',
  'Matte',
  'Fashion Fever',
  'HM',
];

const shirtingFabrics: FabricItem[] = [
  {
    id: 'cotton-poplin',
    name: 'Cotton Poplin',
    composition: '100% Cotton',
    weight: '100–130 gsm',
    description:
      'The foundation of formal shirting — crisp, smooth, and breathable. Available in solids, stripes, and checks. Sourced from Bhiwandi, Thane.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16be11385-1772903382975.png',
    alt: 'Crisp white cotton poplin shirting fabric stacked neatly in a bright airy textile showroom in Bhiwandi',
    colSpan: 'lg:col-span-1',
  },
  {
    id: 'oxford-weave',
    name: 'Oxford Weave',
    composition: '100% Cotton',
    weight: '140–170 gsm',
    description:
      'Textured basket-weave cotton with a distinctive grid pattern. The choice for business-casual and smart shirts.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_117a434b5-1772084683675.png',
    alt: 'Blue oxford weave shirting fabric with fine basket-weave grid texture in a well-lit textile warehouse',
    colSpan: 'lg:col-span-2',
    rowSpan: 'lg:row-span-2',
  },
  {
    id: 'dobby',
    name: 'Dobby Fabric',
    composition: '60% Cotton / 40% Polyester',
    weight: '120–150 gsm',
    description:
      'Woven geometric micro-patterns give dobby fabric a subtle texture and visual interest for dress shirts.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16130092c-1777223188384.png',
    alt: 'Light blue dobby shirting fabric with subtle geometric woven pattern in a bright professional textile setting',
    colSpan: 'lg:col-span-1',
  },
  {
    id: 'chambray',
    name: 'Chambray',
    composition: '100% Cotton',
    weight: '110–140 gsm',
    description:
      'Soft, casual-looking cotton with a denim-like appearance. Versatile for both casual and smart-casual shirts.',
    image: 'https://images.unsplash.com/photo-1592207998273-a7bee6c6fc9b',
    alt: 'Indigo blue chambray shirting fabric with soft denim-like appearance in natural daylight setting',
    colSpan: 'lg:col-span-1',
  },
  {
    id: 'linen-cotton',
    name: 'Linen Cotton',
    composition: '55% Linen / 45% Cotton',
    weight: '130–160 gsm',
    description:
      'Natural linen blended with cotton for reduced creasing. Breathable and ideal for summer and resort collections.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bd58957b-1773126341673.png',
    alt: 'Natural beige linen cotton shirting fabric with textured surface in a bright airy textile setting',
    colSpan: 'lg:col-span-1',
  },
  {
    id: 'jacquard',
    name: 'Jacquard',
    composition: '65% Cotton / 35% Polyester',
    weight: '150–180 gsm',
    description:
      'Richly patterned jacquard weave for premium dress shirts and occasion wear. Intricate designs woven into the fabric.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c3e3f3bc-1777223189433.png',
    alt: 'White jacquard shirting fabric with subtle woven floral pattern in an elegant professional studio setting',
    colSpan: 'lg:col-span-1',
  },
];

type ProductsContentProps = {
  initialCategory?: 'suiting' | 'shirting';
  /** When true, hides the category tab switcher (used on dedicated /suiting and /shirting pages). */
  lockCategory?: boolean;
  heading?: string;
  eyebrow?: string;
  intro?: string;
};

export default function ProductsContent({
  initialCategory: categoryOverride,
  lockCategory = false,
  heading = 'Our Collections',
  eyebrow = 'Fabric Catalog',
  intro = 'Browse our wholesale suiting and shirting fabric catalog. Submit an inquiry for pricing, availability, and swatches.',
}: ProductsContentProps) {
  const searchParams = useSearchParams();
  const initialCategory =
    categoryOverride ?? (searchParams.get('category') === 'shirting' ? 'shirting' : 'suiting');
  const [activeCategory, setActiveCategory] = useState<'suiting' | 'shirting'>(
    initialCategory as 'suiting' | 'shirting'
  );
  const [inquiryFabric, setInquiryFabric] = useState<string | null>(null);
  const query = (searchParams.get('query') || '').trim().toLowerCase();

  const fabrics = (activeCategory === 'suiting' ? suitingFabrics : shirtingFabrics).filter(
    (fabric) => {
      if (!query) return true;
      return (
        fabric.name.toLowerCase().includes(query) ||
        fabric.composition.toLowerCase().includes(query) ||
        fabric.description.toLowerCase().includes(query)
      );
    }
  );

  return (
    <>
      {/* Page Hero */}
      <section className="relative bg-foreground pt-32 pb-16 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_140561897-1774719752264.png"
            alt="Fabric rolls in dark atmospheric textile warehouse setting"
            fill
            priority
            className="object-cover"
            sizes="100vw"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/80 to-foreground" />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-accent" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-accent font-semibold">
              {eyebrow}
            </span>
          </div>
          <h1 className="font-display text-[clamp(2.5rem,6vw,5rem)] text-white leading-tight tracking-tight mb-4">
            {heading}
          </h1>
          <p className="text-white/50 text-sm max-w-md leading-relaxed">{intro}</p>
        </div>
      </section>

      {/* Category Tabs */}
      {!lockCategory && (
        <section className="bg-background border-b border-border sticky top-20 z-40">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-0">
              {(['suiting', 'shirting'] as const).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-8 py-5 text-[11px] font-semibold uppercase tracking-[0.18em] border-b-2 transition-all duration-300 ${
                    activeCategory === cat
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {cat === 'suiting' ? 'Suiting Fabrics' : 'Shirting Fabrics'}
                </button>
              ))}
              <div className="ml-auto hidden sm:flex items-center gap-2 py-4">
                <Link href="/contact" className="btn-primary text-xs py-2.5 px-5">
                  Bulk Inquiry
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {query && (
        <div className="bg-background border-b border-border px-6 md:px-12 py-3">
          <p className="max-w-7xl mx-auto text-xs text-muted-foreground">
            {fabrics.length} result{fabrics.length === 1 ? '' : 's'} for &ldquo;
            {searchParams.get('query')}&rdquo;
          </p>
        </div>
      )}

      {/* Fabric Bento Grid */}
      {/* BENTO AUDIT:
            SUITING (6 cards): Pure Wool cs-2 rs-2, Poly Wool cs-1, Tropical cs-1, Linen cs-1, Cashmere cs-1, Tweed cs-1
            Row 1: [col1-2: Pure Wool] [col3: Poly Wool]
            Row 2: [col1-2: Pure Wool(cont)] [col3: Tropical]
            Row 3: [col1: Linen] [col2: Cashmere] [col3: Tweed]
            Placed 6/6 ✓
             SHIRTING (6 cards): Cotton Poplin cs-1, Oxford cs-2 rs-2, Dobby cs-1, Chambray cs-1, Linen Cotton cs-1, Jacquard cs-1
            Row 1: [col1: Cotton Poplin] [col2-3: Oxford]
            Row 2: [col1: Dobby] [col2-3: Oxford(cont)]
            Row 3: [col1: Chambray] [col2: Linen Cotton] [col3: Jacquard]
            Placed 6/6 ✓
         */}
      <section className="bg-background py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fabrics.map((fabric) => (
              /* STEP 4 — grid card div */
              <div
                key={fabric.id}
                className={`group relative overflow-hidden bg-card border border-border flex flex-col ${fabric.colSpan ?? ''} ${fabric.rowSpan ?? ''}`}
              >
                {/* Image */}
                <div
                  className={`relative overflow-hidden ${fabric.rowSpan ? 'min-h-[400px] flex-1' : 'h-52'}`}
                >
                  <AppImage
                    src={fabric.image}
                    alt={fabric.alt}
                    fill
                    className="object-cover transition-transform duration-1000 group-hover:scale-[1.05]"
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  {/* Composition badge */}
                  <div className="absolute top-4 left-4">
                    <span className="text-[9px] font-semibold uppercase tracking-wider bg-primary/90 text-white px-2.5 py-1">
                      {fabric.composition}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col justify-between gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-display text-xl font-semibold text-foreground">
                        {fabric.name}
                      </h3>
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                        {fabric.weight}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {fabric.description}
                    </p>
                  </div>
                  <button
                    onClick={() => setInquiryFabric(fabric.name)}
                    className="btn-outline self-start text-xs py-2 px-4"
                  >
                    Inquire
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M7 17L17 7M7 7h10v10" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Other trade qualities */}
          <div className="mt-12 border border-border bg-card p-8 md:p-10">
            <h2 className="font-display text-xl text-foreground font-semibold mb-2">
              Other {activeCategory === 'suiting' ? 'Suiting' : 'Shirting'} Qualities We Trade
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-2xl">
              Beyond our core catalogue above, we regularly source and trade these popular market
              qualities. Ask us for current stock, composition and pricing.
            </p>
            <div className="flex flex-wrap gap-2">
              {(activeCategory === 'suiting' ? suitingQualityNames : shirtingQualityNames).map(
                (qualityName) => (
                  <span
                    key={qualityName}
                    className="text-xs font-medium text-foreground bg-muted border border-border px-3 py-1.5"
                  >
                    {qualityName}
                  </span>
                )
              )}
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-6 border border-border bg-muted p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl text-foreground mb-2">
                Need a custom composition?
              </h3>
              <p className="text-muted-foreground text-sm">
                We source specific fabric blends on request for bulk orders. Contact our team
                directly.
              </p>
            </div>
            <Link href="/contact" className="btn-primary whitespace-nowrap">
              Submit Requirement
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Inquiry Modal */}
      {inquiryFabric && (
        <InquiryModal fabric={inquiryFabric} onClose={() => setInquiryFabric(null)} />
      )}
    </>
  );
}

function InquiryModal({ fabric, onClose }: { fabric: string; onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', phone: '', quantity: '', message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submit — connect to backend
    trackEvent('generate_lead', {
      form_name: 'product_fabric_inquiry',
      fabric_name: fabric,
    });
    setSubmitted(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card w-full max-w-lg border border-border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">
              Inquiry for
            </p>
            <h3 className="font-display text-xl text-foreground font-semibold">{fabric}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
              <div className="w-14 h-14 bg-primary/10 flex items-center justify-center">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <p className="font-display text-xl text-foreground">Inquiry Sent!</p>
              <p className="text-muted-foreground text-sm">
                We&apos;ll contact you within 24 hours.
              </p>
              <button onClick={onClose} className="btn-primary mt-2">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="form-label">Your Name</label>
                <input
                  name="name"
                  type="text"
                  placeholder="Full name"
                  className="form-input"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="form-label">Company Name</label>
                <input
                  name="company"
                  type="text"
                  placeholder="Your business"
                  className="form-input"
                  value={form.company}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Phone</label>
                  <input
                    name="phone"
                    type="tel"
                    placeholder="+91 XXXXX XXXXX"
                    className="form-input"
                    value={form.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Quantity (metres)</label>
                  <input
                    name="quantity"
                    type="text"
                    placeholder="e.g. 500m"
                    className="form-input"
                    value={form.quantity}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Additional Requirements</label>
                <textarea
                  name="message"
                  rows={3}
                  placeholder="Colours, width, delivery..."
                  className="form-input resize-none"
                  value={form.message}
                  onChange={handleChange}
                />
              </div>
              <button type="submit" className="btn-primary w-full justify-center">
                Send Inquiry
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
